import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AppSidebar } from "@/components/chat/AppSidebar";
import { KnowledgeSidebar } from "@/components/chat/KnowledgeSidebar";
import { CurrentFocusContainer, CurrentFocusContainerHandle } from "@/components/chat/CurrentFocusContainer";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { addBreadcrumb } from "@/lib/logger";
import type { ActivityView } from "@/components/chat/ActivityBar";
import type { KnowledgeView } from "@/components/chat/KnowledgeBar";
import { useSession } from "@/hooks/useSession";


const Workspace = () => {
  const location = useLocation();
  const { user, isLoading } = useSession();
  const [activeView, setActiveView] = useState<ActivityView>("history");
  
  // Load panel sizes from localStorage or use defaults
  const [sidebarSize, setSidebarSize] = useState(() => {
    const saved = localStorage.getItem('ui_sidebar_size');
    return saved ? Math.min(parseFloat(saved), 30) : 20; // Cap at 30
  });
  const [savedSidebarSize, setSavedSidebarSize] = useState(() => {
    const saved = localStorage.getItem('ui_sidebar_size');
    return saved ? Math.min(parseFloat(saved), 30) : 20; // Cap at 30
  });
  
  const [knowledgeView, setKnowledgeView] = useState<KnowledgeView>("chat");
  const [knowledgeSidebarSize, setKnowledgeSidebarSize] = useState(() => {
    const saved = localStorage.getItem('ui_knowledge_sidebar_size');
    return saved ? Math.min(parseFloat(saved), 30) : 20; // Cap at 30
  });
  const [savedKnowledgeSidebarSize, setSavedKnowledgeSidebarSize] = useState(() => {
    const saved = localStorage.getItem('ui_knowledge_sidebar_size');
    return saved ? Math.min(parseFloat(saved), 30) : 20; // Cap at 30
  });
  
  const currentFocusRef = useRef<CurrentFocusContainerHandle>(null);

  const handleViewChange = useCallback((view: ActivityView | null) => {
    if (view === null) {
      // Collapse: save current size and set to 0
      setSavedSidebarSize(sidebarSize);
      setSidebarSize(0);
      setActiveView("history"); // Keep a valid state
    } else if (activeView === view && sidebarSize > 0) {
      // Toggle off: collapse
      setSavedSidebarSize(sidebarSize);
      setSidebarSize(0);
    } else {
      // Open or switch view
      setActiveView(view);
      if (sidebarSize === 0) {
        setSidebarSize(savedSidebarSize);
      }
    }
  }, [activeView, sidebarSize, savedSidebarSize]);

  const handleKnowledgeViewChange = useCallback((view: KnowledgeView | null) => {
    if (view === null) {
      // Collapse: save current size and set to 0
      setSavedKnowledgeSidebarSize(knowledgeSidebarSize);
      setKnowledgeSidebarSize(0);
      setKnowledgeView("chat"); // Keep a valid state
    } else if (knowledgeView === view && knowledgeSidebarSize > 0) {
      // Toggle off: collapse
      setSavedKnowledgeSidebarSize(knowledgeSidebarSize);
      setKnowledgeSidebarSize(0);
    } else {
      // Open or switch view
      setKnowledgeView(view);
      if (knowledgeSidebarSize === 0) {
        setKnowledgeSidebarSize(savedKnowledgeSidebarSize);
      }
    }
  }, [knowledgeView, knowledgeSidebarSize, savedKnowledgeSidebarSize]);

  useEffect(() => {
    // Listen for Focused Mode changes
    const handleFocusedMode = (e: CustomEvent) => {
      if (e.detail.closeHistoryPanel) {
        handleViewChange(null);
      }
      if (e.detail.collapseKnowledgeBar) {
        handleKnowledgeViewChange(null);
      }
    };
    
    window.addEventListener('applyFocusedMode', handleFocusedMode as EventListener);

    return () => {
      window.removeEventListener('applyFocusedMode', handleFocusedMode as EventListener);
    };
  }, [handleViewChange, handleKnowledgeViewChange]);

  // Handle initial focus text from landing page
  useEffect(() => {
    const state = location.state as { initialFocus?: string } | null;
    if (state?.initialFocus && currentFocusRef.current) {
      // TODO: Set the initial focus text in the CurrentFocusContainer
      // This will require adding a method to CurrentFocusContainerHandle
      console.log('Initial focus:', state.initialFocus);
    }
  }, [location.state]);

  const handleSidebarResize = (size: number) => {
    setSidebarSize(size);
    if (size > 0) {
      localStorage.setItem('ui_sidebar_size', size.toString());
    }
  };

  const handleKnowledgeSidebarResize = (size: number) => {
    setKnowledgeSidebarSize(size);
    if (size > 0) {
      localStorage.setItem('ui_knowledge_sidebar_size', size.toString());
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-background">
      <ResizablePanelGroup direction="horizontal">
        {/* Left Sidebar */}
        <ResizablePanel
          id="sidebar"
          order={1}
          defaultSize={sidebarSize}
          minSize={0}
          maxSize={30}
          collapsible={true}
          collapsedSize={0}
          onResize={handleSidebarResize}
        >
          {sidebarSize > 0 && (
            <AppSidebar 
              activeView={activeView}
              onViewChange={handleViewChange}
              currentFocusRef={currentFocusRef}
            />
          )}
        </ResizablePanel>

        {sidebarSize > 0 && (
          <ResizableHandle 
            withHandle 
            className="w-1 bg-border hover:bg-primary/20 transition-colors"
          />
        )}

        {/* Main Content */}
        <ResizablePanel 
          id="main-content" 
          order={2} 
          defaultSize={100 - sidebarSize - knowledgeSidebarSize}
          minSize={30}
        >
          <CurrentFocusContainer 
            ref={currentFocusRef}
            onOpenHistory={() => handleViewChange("history")}
          />
        </ResizablePanel>

        {knowledgeSidebarSize > 0 && (
          <ResizableHandle 
            withHandle 
            className="w-1 bg-border hover:bg-primary/20 transition-colors"
          />
        )}

        {/* Right Sidebar (Knowledge) */}
        <ResizablePanel
          id="knowledge-sidebar"
          order={3}
          defaultSize={knowledgeSidebarSize}
          minSize={0}
          maxSize={30}
          collapsible={true}
          collapsedSize={0}
          onResize={handleKnowledgeSidebarResize}
        >
          {knowledgeSidebarSize > 0 && (
            <KnowledgeSidebar 
              activeView={knowledgeView}
              onViewChange={handleKnowledgeViewChange}
            />
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

export default Workspace;
